import { Link } from "@tanstack/react-router";
import { Compass, Plus } from "lucide-react";
import type { ReactNode } from "react";
import { Tooltip } from "./Tooltip";

export type SidebarProps = {
  logo: ReactNode;
  children?: ReactNode;
  onAddClick?: () => void;
  discoverHref?: string;
  onDiscoverClick?: () => void;
  isDiscoverActive?: boolean;
  className?: string;
};

export type SidebarItemProps = {
  label: string;
  subtitle?: string;
  shortcut?: string;
  children?: ReactNode;
  isActive?: boolean;
  showTooltip?: boolean;
  href?: string;
  onClick?: () => void;
  className?: string;
};

export function Sidebar({
  logo,
  children,
  onAddClick,
  discoverHref,
  onDiscoverClick,
  isDiscoverActive = false,
  className = "",
}: SidebarProps) {
  return (
    <aside className={`flex flex-col w-16 min-w-16 h-full bg-surface-950 ${className}`}>
      <div className="h-16 flex items-center justify-center shrink-0">{logo}</div>
      <nav className="flex-1 overflow-y-auto px-2 flex flex-col items-center gap-2">
        {children}
        {onAddClick && (
          <Tooltip content="Create Club" side="right" fullWidth>
            <button
              onClick={onAddClick}
              className="flex items-center justify-center w-10 h-10 mx-auto rounded-lg transition-all border-2 border-dashed border-surface-600 hover:border-brand-500 hover:bg-surface-800 group"
            >
              <Plus className="w-4 h-4 text-surface-400 group-hover:text-brand-400 transition-colors" />
            </button>
          </Tooltip>
        )}
        {(discoverHref || onDiscoverClick) && (
          <Tooltip content="Discover Clubs" side="right" fullWidth>
            {discoverHref ? (
              <Link
                to={discoverHref}
                className={`flex items-center justify-center w-10 h-10 mx-auto rounded-lg transition-all group ${
                  isDiscoverActive
                    ? "ring-2 ring-brand-500 bg-surface-800"
                    : "bg-surface-800 hover:bg-surface-700"
                }`}
              >
                <Compass
                  className={`w-4 h-4 transition-colors ${
                    isDiscoverActive
                      ? "text-brand-400"
                      : "text-surface-400 group-hover:text-brand-400"
                  }`}
                />
              </Link>
            ) : (
              <button
                onClick={onDiscoverClick}
                className={`flex items-center justify-center w-10 h-10 mx-auto rounded-lg transition-all group ${
                  isDiscoverActive
                    ? "ring-2 ring-brand-500 bg-surface-800"
                    : "bg-surface-800 hover:bg-surface-700"
                }`}
              >
                <Compass
                  className={`w-4 h-4 transition-colors ${
                    isDiscoverActive
                      ? "text-brand-400"
                      : "text-surface-400 group-hover:text-brand-400"
                  }`}
                />
              </button>
            )}
          </Tooltip>
        )}
      </nav>
    </aside>
  );
}

export function SidebarItem({
  label,
  subtitle,
  shortcut,
  children,
  isActive = false,
  showTooltip = true,
  href,
  onClick,
  className = "",
}: SidebarItemProps) {
  const classes = `
    group relative flex items-center justify-center w-12 h-12 transition-all cursor-pointer
    ${className}
  `;

  const inner = (
    <div
      className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm shrink-0 overflow-hidden ${children ? "" : "bg-surface-700"}`}
    >
      {children || shortcut || label.charAt(0).toUpperCase()}
    </div>
  );

  const pill = (
    <span
      className={`absolute -left-2 top-1/2 -translate-y-1/2 w-1 rounded-r-full transition-all duration-150 ease-out ${
        isActive
          ? "h-8 bg-brand-500 opacity-100"
          : "h-4 bg-surface-400 opacity-0 group-hover:opacity-100"
      }`}
    />
  );

  const button = href ? (
    <Link to={href} className={classes} draggable={false}>
      {pill}
      {inner}
    </Link>
  ) : (
    <button onClick={onClick} className={classes}>
      {pill}
      {inner}
    </button>
  );

  if (!showTooltip) {
    return button;
  }

  return (
    <Tooltip
      content={
        <div>
          <p className="font-semibold text-white">{label}</p>
          {subtitle && <p className="text-xs text-surface-400">{subtitle}</p>}
        </div>
      }
      side="right"
      fullWidth
    >
      {button}
    </Tooltip>
  );
}
